import { useEffect, useRef, useState } from 'react';
import type { SelectOption } from '@/data/content.ts';
import { SITE, TURNSTILE } from '@/data/site.ts';

export interface FormFieldConfig {
  readonly name: string;
  readonly label: string;
  readonly type: 'text' | 'email' | 'tel' | 'textarea' | 'select';
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly options?: readonly SelectOption[];
}

interface TurnstileRenderOptions {
  readonly sitekey: string;
  readonly action?: string;
  readonly theme?: 'light' | 'dark' | 'auto';
  readonly callback?: (token: string) => void;
  readonly 'error-callback'?: (errorCode?: string) => void;
  readonly 'expired-callback'?: () => void;
}

interface TurnstileApi {
  render: (
    container: string | HTMLElement,
    options: TurnstileRenderOptions,
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
  getResponse: (widgetId?: string) => string;
}

function getTurnstile(): TurnstileApi | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

/**
 * Contact form that posts to the companion Cloudflare Worker (worker/index.ts).
 *
 *  - The Worker's target endpoint is cross-origin and may return no CORS
 *    headers, so the request is sent with mode: 'no-cors'. The response is
 *    opaque and its status cannot be read — a resolved promise only means
 *    the request left the browser. A network-level failure still rejects,
 *    surfaced here so the visitor can retry.
 *  - The form is hidden with `visibility` (via .form-shell--submitted)
 *    rather than display/hidden so it keeps occupying its box — the
 *    acknowledgement sits in the same grid cell, so page height never
 *    changes on submit.
 *
 * Anti-spam: Cloudflare Turnstile (managed challenge, verified server-side
 * by the Worker) plus an invisible honeypot field.
 */
export function ContactForm({
  action,
  fields,
  submitLabel,
  successTitle,
  successBody,
  idPrefix = '',
  turnstileAction = 'contact',
  showArrow = true,
  buttonClassName = 'btn btn--primary',
}: {
  action: string;
  fields: readonly FormFieldConfig[];
  submitLabel: string;
  successTitle: string;
  successBody: string;
  idPrefix?: string;
  turnstileAction?: string;
  showArrow?: boolean;
  buttonClassName?: string;
}) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');

  const successRef = useRef<HTMLDivElement | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  const fieldId = (name: string) => `${idPrefix}${name}`;

  useEffect(() => {
    let active = true;
    let timerId: ReturnType<typeof setInterval> | null = null;

    function renderWidget() {
      if (!active || !turnstileContainerRef.current) return;
      const turnstile = getTurnstile();
      if (turnstile) {
        if (timerId) {
          clearInterval(timerId);
          timerId = null;
        }
        if (widgetIdRef.current) {
          try {
            turnstile.remove(widgetIdRef.current);
          } catch {}
          widgetIdRef.current = null;
        }
        try {
          const id = turnstile.render(turnstileContainerRef.current, {
            sitekey: TURNSTILE.siteKey,
            action: turnstileAction,
            theme: 'light',
            callback: (token: string) => {
              if (!active) return;
              setTurnstileToken(token);
              setTurnstileError(null);
            },
            'expired-callback': () => {
              if (!active) return;
              setTurnstileToken(null);
            },
            'error-callback': () => {
              if (!active) return;
              setTurnstileError(
                'Verification check failed. Please refresh or try again.',
              );
            },
          });
          widgetIdRef.current = id;
        } catch (e) {
          console.error('Failed to render Turnstile widget', e);
        }
      }
    }

    if (typeof window !== 'undefined') {
      if (getTurnstile()) {
        renderWidget();
      } else {
        timerId = setInterval(() => {
          if (getTurnstile()) {
            renderWidget();
          }
        }, 100);
      }
    }

    return () => {
      active = false;
      if (timerId) clearInterval(timerId);
      const turnstile = getTurnstile();
      if (widgetIdRef.current && turnstile) {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [turnstileAction]);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state === 'sending' || state === 'sent') return;

    const form = event.currentTarget;

    // Honeypot defense: silently drop bot submissions without hitting the Worker.
    if (honeypot.trim().length > 0) {
      setState('sent');
      form.reset();
      return;
    }

    // Require Turnstile verification before submitting.
    if (!turnstileToken) {
      setTurnstileError(
        'Please complete the verification check before submitting.',
      );
      return;
    }

    setState('sending');
    setTurnstileError(null);

    try {
      const formData = new FormData(form);
      formData.set('cf-turnstile-response', turnstileToken);

      await fetch(action, {
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      });
      setState('sent');
      form.reset();
      requestAnimationFrame(() =>
        successRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        }),
      );
    } catch {
      setState('error');
    } finally {
      // Single-use token lifecycle: reset widget on completion so visitor can retry.
      const turnstile = getTurnstile();
      if (widgetIdRef.current && turnstile) {
        try {
          turnstile.reset(widgetIdRef.current);
        } catch {}
      }
      setTurnstileToken(null);
    }
  }

  const sent = state === 'sent';

  return (
    <div className={`form-shell ${sent ? 'form-shell--submitted' : ''}`.trim()}>
      <form
        action={action}
        method="POST"
        onSubmit={onSubmit}
        noValidate={false}
      >
        {/* Honeypot field: invisible to humans, catches automated bot scripts */}
        <div
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '-9999px',
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        >
          <label htmlFor={fieldId('company_url')}>Leave this field blank</label>
          <input
            id={fieldId('company_url')}
            name="company_url"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        {fields.map((field) => (
          <div className="form-group" key={field.name}>
            <label htmlFor={fieldId(field.name)}>{field.label}</label>

            {field.type === 'textarea' ? (
              <textarea
                id={fieldId(field.name)}
                name={field.name}
                {...(field.placeholder
                  ? { placeholder: field.placeholder }
                  : {})}
                {...(field.required ? { required: true } : {})}
              />
            ) : field.type === 'select' ? (
              <select
                id={fieldId(field.name)}
                name={field.name}
                defaultValue=""
                {...(field.required ? { required: true } : {})}
              >
                <option value="">{field.placeholder ?? 'Select one...'}</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={fieldId(field.name)}
                name={field.name}
                type={field.type}
                {...(field.placeholder
                  ? { placeholder: field.placeholder }
                  : {})}
                {...(field.required ? { required: true } : {})}
              />
            )}
          </div>
        ))}

        <div ref={turnstileContainerRef} className="form-turnstile" />

        {turnstileError && (
          <p className="form-error" role="alert">
            {turnstileError}
          </p>
        )}

        <button
          type="submit"
          className={buttonClassName}
          style={{ width: '100%', justifyContent: 'center' }}
          disabled={state === 'sending'}
        >
          {state === 'sending' ? 'Sending…' : submitLabel}
          {showArrow && (
            <>
              {' '}
              <span className="btn__arrow" aria-hidden="true">
                &rarr;
              </span>
            </>
          )}
        </button>

        {state === 'error' && (
          <p className="form-error" role="alert">
            Something went wrong sending your request. Please try again, or
            reach us directly at {SITE.phone}.
          </p>
        )}
      </form>

      <div
        className={`form-success ${sent ? 'form-success--visible' : ''}`.trim()}
        role="status"
        aria-live="polite"
        hidden={!sent}
        ref={successRef}
      >
        <div>
          <div className="form-success__mark" aria-hidden="true">
            &#10003;
          </div>
          <h3>{successTitle}</h3>
          <p>{successBody}</p>
        </div>
      </div>
    </div>
  );
}
